<template>
  <div class="main-content">
    <div class="section__content section__content--p30">
      <div class="container-fluid job-info-container">
        <v-card class="p-b-40 p-t-30 p-l-30 p-r-30">
          <div class="el-loading-mask" style="bottom: 0" v-if="loading">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>
          <!-- Title -->
          <div class="row">
            <div class="col-md-9 header">
              <h3 class="title-7 p-b-10">
                <v-btn
                  text
                  icon
                  class="m-0"
                  @click="$router.back()">
                  <i class="zmdi zmdi-long-arrow-left"></i>
                </v-btn>
                {{ wf.name }}
              </h3>
              <span class="p-l-20 status--gray">ID={{ wf.id }}</span>
            </div>
            <div class="col-md-3 text-right">
              <v-btn
                color="error"
                class="m-0 white--text"
                @click="openDeleteDialog()">
                <i class="fa fa-trash p-r-10"></i> Delete
              </v-btn>
            </div>
          </div>
          <v-divider></v-divider>
          <!-- Status and general Info -->
          <div class="row">
            <div class="col-md-2">
              <div class="info-title">Status</div>
              <i class="fa fa-circle-notch fa-spin job-status--finished"
                 v-if="wf.state === 'active'">
              </i>
              <i class="fa fa-check-circle job-status--finished"
                 v-if="wf.state === 'finished'">
              </i>
              <i class="fa fa-exclamation-circle job-status--failed"
                 v-if="wf.state === 'error'">
              </i>
              <span class="p-l-10">{{ wf.state }}</span>
            </div>
            <div class="col-md-3">
              <div class="info-title">Profile</div>
              <div class="p-b-5" v-if="!wf.profile.name">
                {{ wf.profile.name || 'N/A' }}
              </div>
              <v-btn
                v-else
                small
                outline
                class="m-0"
                @click="profileInfoDialog=true">
                {{ wf.profile.name | truncate(25) }}
              </v-btn>
            </div>
            <div class="col-md-2">
              <div class="info-title">
                <i class="zmdi zmdi-calendar p-r-10"></i>Created
              </div>
              <div>
                {{ formatTime(wf.createTime) }}
              </div>
            </div>
            <div class="col-md-2">
              <div class="info-title">
                <i class="zmdi zmdi-calendar p-r-10"></i>Updated
              </div>
              <div>
                {{ formatTime(wf.updateTime) }}
              </div>
            </div>
            <div class="col-md-2" v-if="wf.once">
              <div class="info-title">
                Perform once
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon color="grey" v-on="on" size="20">
                      fa-question-circle
                    </v-icon>
                  </template>
                  <span>In this case, the specified directory is scanned once immediately
                            after the watch folder is created.
                        </span>
                </v-tooltip>
              </div>
              <div>
                <i class="zmdi zmdi-check"></i>
              </div>
            </div>
          </div>
          <v-divider></v-divider>
          <!-- Input & Output -->
          <div class="row">
            <div class="col-md-12">
              <!-- Input -->
              <table class="job-output m-t-50">
                <tbody>
                  <tr>
                    <td class="column-title">
                      FTP folder
                      </td>
                    <td class="link" colspan="2">
                      <a :href="wf.url" target="_blank">
                        {{ wf.url }}
                      </a>
                      <i @click="handleCopy(wf.url)"
                         class="zmdi zmdi-copy m-l-10">
                      </i>
                    </td>
                  </tr>
                  <tr v-if="wf.error">
                    <td class="column-error">
                      Error
                    </td>
                    <td colspan="2" class="column-error">
                      {{ wf.error }}
                    </td>
                  </tr>
                </tbody>
              </table>
              <!-- Outputs -->
              <table class="job-output m-t-50" :key="output.id" v-for="(output, i) of wf.outputs">
                <tbody>
                <tr v-if="i === 0" style="background-color: #eee">
                  <td colspan="3" class="text-center body-2">
                    All tasks created by the Watch Folder appear in the common task list
                    (the scanning frequency is every minute).
                    See Section <a href="#" @click.prevent="goToJobs()">Encoding Jobs</a>
                  </td>
                </tr>
                <tr>
                  <td class="column-title">
                    Output #{{ i+1 }}
                  </td>
                  <td colspan="2">
                    {{ output.name }}
                  </td>
                </tr>
                <tr :key="url" v-for="(url, urlIndex) of output.urls">
                  <td>
                    <i class="zmdi zmdi-archive p-r-10" v-if="output.zip"></i>
                    Destination URL <span v-if="output.urls.length>1">#{{ urlIndex+1 }}</span></td>
                  <td class="link" colspan="2">
                    <a :href="outputURL(url)"
                       target="_blank"
                       v-if="outputURL(url) !== 'origin'">
                      {{ outputURL(url) }}
                    </a>
                    <span v-else>Origin server</span>
                    <i @click="handleCopy(outputURL(url))"
                       class="zmdi zmdi-copy m-l-10"
                       v-if="outputURL(url) !== 'origin'">
                    </i>
                  </td>
                </tr>
                <tr>
                  <td>Format</td>
                  <td colspan="2">
                    {{ formats[output.format] }}
                    <span v-if="output.format==='segmented_hls'">v{{ output.hlsVersion || 3 }}</span>
                    <span class="p-l-10" v-if="output.segmentSize">
                      segment duration: {{ output.segmentSize }} sec
                    </span>
                  </td>
                </tr>
                <tr v-if="output.drm">
                  <td>
                    <i class="zmdi zmdi-lock p-r-10" v-if="output.drm"></i>
                    DRM
                  </td>
                  <td>
                    {{ drmMap[output.drm] }}
                  </td>
                  <td>
                    <v-btn class="m-0 float-right" outline small @click="toggleDRMInfo(i)">
                      {{ output.drmInfo ? 'Hide' : 'Show'}} details
                    </v-btn>
                  </td>
                </tr>
                <tr class="drm-row" v-show="output.drm==='widevine' && output.drmInfo">
                  <td>Key Server URL</td>
                  <td colspan="2">
                    <a :href="output.widevine_license_server" target="_blank">
                      {{ output.widevine_license_server }}
                    </a>
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='widevine' && output.drmInfo">
                  <td>Provider</td>
                  <td colspan="2">
                    {{ output.widevine_provider }}
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='widevine' && output.drmInfo">
                  <td>Key</td>
                  <td colspan="2">
                    {{ output.widevine_key }}
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='widevine' && output.drmInfo">
                  <td>IV</td>
                  <td colspan="2">
                    {{ output.widevine_iv }}
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='widevine' && output.drmInfo">
                  <td>Package Name</td>
                  <td colspan="2">
                    {{ output.widevine_package_name }}
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='playready' && output.drmInfo">
                  <td>Key Server URL</td>
                  <td colspan="2">
                    <a :href="output.playready_la_url" target="_blank">
                      {{ output.playready_la_url }}
                    </a>
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='playready' && output.drmInfo">
                  <td>Key ID</td>
                  <td colspan="2">
                    {{ output.playready_key_id }}
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='playready' && output.drmInfo">
                  <td>KeySeed</td>
                  <td colspan="2">
                    {{ output.playready_key_seed }}
                  </td>
                </tr>
                <tr class="drm-row" v-if="output.drm==='playready' && output.drmInfo">
                  <td>Content Key</td>
                  <td colspan="2">
                    {{ output.playready_content_key }}
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </v-card>

        <!-- Profile Info Dialog -->
        <v-dialog v-model="profileInfoDialog" max-width="900">
          <v-card class="p-b-15" v-if="!loading">
            <v-card-text>
              <div class="row">
                <div class="col-md-12">
                  <v-btn icon @click="profileInfoDialog = false">
                    <v-icon>zmdi-close</v-icon>
                  </v-btn>
                  <span class="headline align-middle"> {{ wf.profile.name }}</span>
                </div>
              </div>
              <v-divider/>
              <table class="profile-detail-table">
                <tbody>
                <tr>
                  <td><span class="card-item-title">ID</span></td>
                  <td>{{ wf.profile.id }}</td>
                </tr>
                <tr>
                  <td><span class="card-item-title">Audio codec</span></td>
                  <td class="text-uppercase">{{ wf.profile.audioTemplate.codec }}</td>
                </tr>
                <tr>
                  <td><span class="card-item-title">Video codec</span></td>
                  <td>
                    <span class="text-uppercase" v-if="wf.profile.videoTemplate.codec">
                      {{ videoCodecMap[wf.profile.videoTemplate.codec][0] }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td><span class="card-item-title">Video streams</span></td>
                  <td>{{ wf.profile.videoTemplate.streams.length }}</td>
                </tr>
                <tr v-if="wf.profile.videoTemplate.codec.startsWith('h26')">
                  <td><span class="card-item-title">Encoder</span></td>
                  <td>
                    <span v-if="wf.profile.videoTemplate.codec">
                      {{ videoCodecMap[wf.profile.videoTemplate.codec][1]
                      || videoCodecMap[wf.profile.videoTemplate.codec][2] }}
                    </span>
                  </td>
                </tr>
                </tbody>
              </table>
              <v-divider/>
              <table class="profile-detail-table">
                <tbody>
                <tr>
                  <td><span class="card-item-title">Audio channels</span></td>
                  <td>
                    {{ audioChannelsMap[wf.profile.audioTemplate.channels] || 'Same as source' }}
                  </td>
                </tr>
                <tr>
                  <td><span class="card-item-title">Audio bitrate</span></td>
                  <td>{{ wf.profile.audioTemplate.bitrate }} Kbps</td>
                </tr>
                <tr>
                  <td><span class="card-item-title">Sample rate</span></td>
                  <td>
                    <span v-if="wf.profile.audioTemplate.sample_rate">
                      {{ wf.profile.audioTemplate.sample_rate }} Hz
                    </span>
                    <span v-else>Same as source</span>
                  </td>
                </tr>
                </tbody>
              </table>
              <template v-if="wf.profile.videoTemplate.streams.length">
                <table class="profile-streams m-t-15">
                  <thead>
                  <tr>
                    <th class="number-column">Video stream</th>
                    <th v-if="wf.profile.videoTemplate.codec.startsWith('h264')">
                      Profile
                    </th>
                    <th v-if="wf.profile.videoTemplate.codec.startsWith('h265')">
                      Profile
                    </th>
                    <th v-if="wf.profile.videoTemplate.codec.startsWith('h26')">
                      Level
                    </th>
                    <th>Resolution</th>
                    <th>Bitrate, Kbps</th>
                    <th>Resize Mode</th>
                    <th>Frame Rate</th>
                    <th>KeyInt / MinKeyInt</th>
                    <th v-if="wf.profile.videoTemplate.codec.startsWith('h26')">
                      Ref Frames / BFrames
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(stream, i) of wf.profile.videoTemplate.streams" :key="i">
                    <td>#{{ i+1 }}</td>
                    <td v-if="wf.profile.videoTemplate.codec.startsWith('h264')">
                      {{ h264ProfileMap[stream.h264_codec_profile] }}
                    </td>
                    <td v-if="wf.profile.videoTemplate.codec.startsWith('h265')">
                      {{ h265ProfileMap[stream.h264_codec_profile] }}
                    </td>
                    <td v-if="wf.profile.videoTemplate.codec.startsWith('h264')">
                      {{ stream.h264_codec_level || 'Auto' }}
                    </td>
                    <td v-if="wf.profile.videoTemplate.codec.startsWith('h265')">
                      {{ stream.h264_codec_level || 'Auto' }}
                    </td>
                    <td>{{ stream.resolution || 'Same as source'}}</td>
                    <td>{{ stream.bitrate }}</td>
                    <td>{{ resizeModeMap[stream.aspect_ratio] || 'Same as source' }}</td>
                    <td>
                      <span v-if="stream.target_fps">{{ stream.target_fps }} fps</span>
                      <span v-else>Same as source</span>
                    <td>{{ stream.keyframe_int || '0' }}/{{ stream.min_keyframe_int || '0' }}</td>
                    <td v-if="wf.profile.videoTemplate.codec.startsWith('h26')">
                      {{ stream.h264_reference_frames || '0' }}/{{ stream.h264_bframes || '0' }}
                    </td>
                  </tr>
                  </tbody>
                </table>
              </template>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Remove Dialog -->
        <v-dialog v-model="removeDialog" max-width="500" persistent>
          <v-card>
            <v-card-title class="headline">
              Delete Watch Folder?
            </v-card-title>
            <v-card-text>
              <div class="row p-b-20">
                <div class="col-md-12">
                  Are you sure you want to delete the watch folder "{{ wf.name }}"?
                </div>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn depressed small text
                     @click="removeDialog = false">No</v-btn>
              <v-btn depressed small color="error"
                     @click="deleteWF()">Yes</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import { setClipboard } from '@/helpers/utils';
import {
  JOB_TYPES, VIDEO_CODEC_MAP, AUDIO_CHANNELS_MAP,
  H264_CODEC_PROFILE_MAP, H265_CODEC_PROFILE_MAP, RESIZE_MODE_MAP, DRM,
} from '@/components/transcoding/shared/types';
import { processWatchFolder } from '@/store/converters';

export default {
  created() {
    const wfId = this.$route.params.id;
    this.getWatchFolderById(wfId, true);
    this.pollData();
  },
  beforeDestroy() {
    clearInterval(this.polling);
  },
  data() {
    return {
      profileInfoDialog: false,
      removeDialog: false,
      polling: null,
      formats: JOB_TYPES,
      wf: {
        name: '',
        url: '',
        profile: {
          audioTemplate: { name: '' },
          videoVemplate: { name: '' },
        },
        outputs: [],
      },
      videoCodecMap: VIDEO_CODEC_MAP,
      audioChannelsMap: AUDIO_CHANNELS_MAP,
      h264ProfileMap: H264_CODEC_PROFILE_MAP,
      h265ProfileMap: H265_CODEC_PROFILE_MAP,
      resizeModeMap: RESIZE_MODE_MAP,
      drmMap: DRM,
    };
  },
  computed: {
    loading() {
      return this.$store.getters.isDataLoaded;
    },
  },
  methods: {
    pollData() {
      this.polling = setInterval(() => {
        this.getWatchFolderById(this.wf.id, false);
      }, 10000);
    },
    getWatchFolderById(id, nofification = false) {
      if (nofification) {
        this.$store.commit('setPageLoader');
      }
      this.$store.dispatch('watchFolders/getWatchFolderById', id)
        .then((wf) => {
          this.wf = processWatchFolder(wf);
          if (this.wf.state === '') this.wf.state = 'active';
          if (this.wf.state === 'ok') {
            this.wf.state = this.wf.closed ? 'finished' : 'active';
          }
          for (let i = 0; i < wf.outputs.length; i += 1) {
            this.wf.outputs[i].drmInfo = false;
          }
        })
        .catch((errorMsg) => {
          if (nofification) {
            this.$toasted.error(errorMsg, {
              theme: 'toasted-primary',
              position: 'top-center',
              duration: 10000,
            });
            this.$router.push({ name: 'WatchFolderListPage' });
          }
        })
        .finally(() => this.$store.commit('resolvePageLoader'));
    },
    formatTime(time) {
      return moment(time).format('MMM D, YYYY HH:mm');
    },
    handleCopy(text) {
      setClipboard(text);
      this.$toasted.info('URL has been copied', {
        theme: 'toasted-primary',
        position: 'top-center',
        duration: 1000,
      });
    },
    toggleDRMInfo(i) {
      const output = this.wf.outputs[i];
      output.drmInfo = !output.drmInfo;
      Vue.set(this.wf.outputs, i, output);
    },
    outputURL(url) {
      if (url.startsWith('origin')) {
        return url.replace('origin://', 'origin');
      }
      return url;
    },
    openDeleteDialog() {
      this.removeDialog = true;
    },
    deleteWF() {
      this.$store.dispatch('watchFolders/deleteWatchFolder', this.wf.id)
        .then(() => {
          this.$toasted.success('The watch folder has been successfully removed', {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 5000,
          });
          this.$router.push({ name: 'WatchFolderListPage' });
        })
        .catch((errorMsg) => {
          this.removeDialog = false;
          this.$toasted.error(errorMsg, {
            theme: 'toasted-primary',
            position: 'top-center',
            duration: 7000,
          });
        });
    },
    goToJobs() {
      this.$router.push({ name: 'FileTranscodingJobList' });
    },
  },
  watch: {
    '$route.params.id': function (newId, oldId) {
      if (newId !== oldId) {
        this.getWatchFolderById(newId);
      }
    },
  },
};
</script>
